import type { ReactNode } from 'react';
import { getAppVersion, toQueryString } from '@croquiscom-pvt/web2app';
import debounce from 'lodash/debounce';

import { ApiErrorCodes, ApiErrors } from '@common/api-request';
import { Router } from '@common/checkout-utils';
import { isBrowser, isMobile } from '@common/device-manager';
import { Dialog, LoadingManager } from '@common/overlay-manager';
import { loginKakao } from '@common/utils';

import { MESSAGES } from '../constant/messages';
import { PURCHASE_DELAY_TIME } from '../constant/product-option';
import { addCartItemList, createOrderSheet } from '../graphql/select-option.2';
import type { ButtonFlags, ConfirmButtonItem, CreateOrderSheetArgs } from '../type/common.type';
import { PdpCatalogFulfillmentType } from '../type/common.type';
import { analyzeShippingTypes } from '../util/checkers/shipping-type-checker';
import { checkZigzinOrVendorFlexFulfillmentType } from '../util/checkers/zigzin-checker';
import { redirectInquiries } from '../util/executors/inquiry-executor';
import { handleNetworkError } from '../util/executors/network-error-handler-executor';
import { setSesstionStoragePurchasedItems } from '../util/executors/purchased-storage-executor';
import { callZpayEvent } from '../util/executors/zpay-event-executor';
import { showZDSToastInOption } from '../util/toast/zds-toast-in-option';
import type { AddCartOptions, BuildConfirmButtonListProps, CreateOrderSheetConfig } from './confirm-button.core.type';

export const buildConfirmButtonList = ({
  option,
  finalPrice,
  itemCardList,
  queryFrom,
  context,
  config,
  logger,
}: BuildConfirmButtonListProps): ConfirmButtonItem[] | ReactNode => {
  if (!option) {
    return [];
  }
  const isZigzinProduct = checkZigzinOrVendorFlexFulfillmentType(option.catalog_product.fulfillment_type);

  const hasMatchingOptionInZigzin = isZigzinProduct && !!option.catalog_product.matching_catalog_product_info;
  const hasMatchingOptionInGeneral = !isZigzinProduct && !!option.catalog_product.matching_catalog_product_info;

  const handleAddToCart = async (payload: CreateOrderSheetArgs) => {
    const product_item_quantity_list = payload.order_item_list.map((item) => ({
      product_item_id: item.product_item_id,
      quantity_delta: item.quantity,
      additional_option_detail_list: item.additional_option_detail_list,
      product_id: payload.catalog_product_id,
      ...(item.funnel_info && { funnel_info: { type: item.funnel_info.type } }),
      ...(item.grouping_required_quantity && { grouping_required_quantity: item.grouping_required_quantity }),
    }));

    await addCartAPI({ catalog_product_id: payload.catalog_product_id, product_item_quantity_list }, () => {
      const confirmedFrom = context?.from ?? queryFrom;
      const defaultZpayEventAction =
        confirmedFrom === 'similar_products' || context.funnelType === 'cart_list_similar-SIMILAR_IMAGE_PRODUCT'
          ? 'reloadCart'
          : 'addCart';

      // Zpay 이벤트 호출
      callZpayEvent(context?.add_to_cart?.zpay_event_action || defaultZpayEventAction);

      // 웹인 경우 토스트 메시지 출력
      if (!context?.is_zigzag_app) {
        if (context?.add_to_cart?.overrideSuccessMessageInToast) {
          context?.add_to_cart?.overrideSuccessMessageInToast?.();
        } else {
          showZDSToastInOption(MESSAGES.ADD_TO_CART_SUCCESS);
        }
      }
      const shippingAnalysis = analyzeShippingTypes(payload, option);

      const logContext = {
        funnelType: payload.order_item_list[0]?.funnel_info?.type,
        catalogProductId: payload.catalog_product_id ?? '',
        catalogProduct: option.catalog_product,
        matchingCatalogProductInfo: option.catalog_product.matching_catalog_product_info,
        finalPrice,
        confirmItems: payload.order_item_list,

        sendGeneralProductId: hasMatchingOptionInZigzin && shippingAnalysis.hasGeneralItems,
        sendZigzinProductId: hasMatchingOptionInGeneral && shippingAnalysis.hasZigzinItems,
      };

      if (!context?.add_to_cart?.logConfig?.disableLogs) {
        logger.sendAddToCartLogs(logContext, context?.add_to_cart?.logConfig);
      }

      context?.add_to_cart?.onSuccess?.(payload, logContext);
      config?.onClose?.();
    });
  };

  const handlePurchase = debounce(
    (payload: CreateOrderSheetArgs) => {
      const shippingAnalysis = analyzeShippingTypes(payload, option);

      const logContext = {
        isZigzagApp: context?.is_zigzag_app ?? false,
        funnelType: payload.order_item_list[0]?.funnel_info?.type,

        catalogProductId: payload.catalog_product_id ?? '',
        matchingCatalogProductInfo: option.catalog_product.matching_catalog_product_info,

        sendGeneralProductId: hasMatchingOptionInZigzin && shippingAnalysis.hasGeneralItems,
        sendZigzinProductId: hasMatchingOptionInGeneral && shippingAnalysis.hasZigzinItems,
      };

      if (!context?.purchase?.logConfig?.disableLogs) {
        logger.sendPurchaseLogs(logContext, context?.purchase?.logConfig);
      }

      context?.purchase?.onPurchase?.(payload, logContext);
      config?.onClose?.();

      createOrderSheetAPI(payload, config);
    },
    PURCHASE_DELAY_TIME,
    { leading: true, trailing: false }, // 처음 한 번만 즉시 실행하고, 대기 시간 동안의 추가 클릭은 완전히 무시
  );

  const handlePurchaseKaKao = debounce(
    (payload: CreateOrderSheetArgs) => {
      logger.sendPurchaseKakaoUBL(payload.catalog_product_id ?? '');

      context?.purchase?.onKakaoPurchase?.(payload);
      config?.onClose?.();

      createOrderSheetAPI(payload, {
        ...config,
        ignore_login_error: true,
        onNotLoggedIn: () => loginKakao(window.location.href),
      });
    },
    PURCHASE_DELAY_TIME,
    { leading: true, trailing: false }, // 처음 한 번만 즉시 실행하고, 대기 시간 동안의 추가 클릭은 완전히 무시
  );

  const buildConfirmButtonList = () => {
    if (context?.renderButtonList) {
      return context.renderButtonList(itemCardList, {
        handleAddToCart,
        handlePurchase,
        handlePurchaseKakao: handlePurchaseKaKao,
      });
    }

    if (option.catalog_product.trait_list?.some(({ type }) => type === 'SAMPLE')) {
      return BUTTON_SCENARIOS['sample'];
    }

    const is_mobile_naver_ep =
      isMobile() && context?.enable_kakao_purchase_button?.utm_source?.includes('shopping_naver_all');
    const is_kakao_purchase_button_visible = is_mobile_naver_ep && !context?.is_zigzag_app && !context?.is_logged_in;

    if (is_kakao_purchase_button_visible) {
      return BUTTON_SCENARIOS['purchase_with_kakao'];
    }

    const is_on_site = option.catalog_product.fulfillment_type === PdpCatalogFulfillmentType.ON_SITE;
    if (is_on_site || option.flags.is_purchase_only_one_at_time) {
      return BUTTON_SCENARIOS['only_buy_now'];
    }

    if (option.catalog_product.store_deal_banner) {
      return BUTTON_SCENARIOS['only_add_to_cart'];
    }

    return BUTTON_SCENARIOS['default'];
  };

  const BUTTON_SCENARIOS: Record<ButtonFlags, ConfirmButtonItem[]> = {
    default: [
      { text: '장바구니', variant: 'secondaryGray', action: handleAddToCart },
      { text: '바로구매', variant: 'primary', action: handlePurchase },
    ],
    purchase_with_kakao: [
      { text: '장바구니', variant: 'secondaryGray', action: handleAddToCart },
      { text: '바로구매', variant: 'primary', action: handlePurchase },
      { text: '간편구매', variant: 'kakao', action: handlePurchaseKaKao },
    ],
    sample: [{ text: '샘플 신청하기', variant: 'tertiary' as const, action: handlePurchase }],
    only_buy_now: [{ text: '바로구매', variant: 'primary', action: handlePurchase }],
    only_add_to_cart: [{ text: '장바구니', variant: 'primary', action: handleAddToCart }],
  };

  return buildConfirmButtonList();
};

/**
 * 주문 시트 생성 API (미로그인 시 현재 주문 정보 세션스토리지에 저장)
 * @param create_order_sheet_args - 주문 시트 생성 인자
 * @param options - 주문 시트 생성 옵션
 */
export const createOrderSheetAPI = async (
  create_order_sheet_args: CreateOrderSheetArgs,
  options?: CreateOrderSheetConfig,
) => {
  const {
    order_item_list,
    selected_user_account_coupon_id_list = null,
    from,
    catalog_product_id,
  } = create_order_sheet_args;

  try {
    const is_in_app = Boolean(getAppVersion());
    const appAction = window.location.pathname.includes('select-option') ? 'createOrderSheet' : undefined;

    LoadingManager.open({ isMiniLoader: true });

    const result = await createOrderSheet(
      { input: { order_item_list, selected_user_account_coupon_id_list } },
      {
        show_alert: options?.show_alert ?? false,
        ignore_error: options?.ignore_login_error ? ApiErrors.NOT_LOGGED_IN : undefined,
      },
    );

    if (result.errors) {
      const message = result.errors?.[0].message;
      const not_logged_in =
        message === ApiErrors.NOT_LOGGED_IN || result.errors?.[0].extensions?.code === 'not_logged_in';

      if (not_logged_in) {
        setSesstionStoragePurchasedItems(catalog_product_id ?? '', JSON.stringify(create_order_sheet_args));
        options?.onNotLoggedIn?.();
      } else {
        showZDSToastInOption(MESSAGES.ORDER_SHEET_ERROR);
      }
      return;
    }

    if (isBrowser()) {
      if (is_in_app) {
        Router.open(`/order-sheets/${result.data.createOrderSheet}?${toQueryString({ from })}`, {
          title: '결제',
          type: 'checkout',
          action: appAction,
        });
      } else {
        Router.push(`/order-sheets/${result.data.createOrderSheet}?${toQueryString({ from })}`, {
          title: '결제',
          type: 'checkout',
        });
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const retried = await handleNetworkError(error, async () =>
      createOrderSheetByOrderItemListAPI({ order_item_list, selected_user_account_coupon_id_list, from }),
    );
    if (retried) {
      return;
    }

    await Dialog.alert(error?.description || MESSAGES.UNKNOWN_ERROR);
    showZDSToastInOption(MESSAGES.ORDER_SHEET_ERROR);

    if (error.extensions?.code === 'seller_blocked_user_validation_failed' && catalog_product_id) {
      redirectInquiries(catalog_product_id);
    }
  } finally {
    LoadingManager.close();
  }
};

/**
 * 주문 시트 생성 API
 * @param args - 주문 시트 생성 인자
 * @param options - 주문 시트 생성 옵션
 */
const createOrderSheetByOrderItemListAPI = async (args: CreateOrderSheetArgs) => {
  const { order_item_list, selected_user_account_coupon_id_list, from } = args;
  try {
    LoadingManager.open({ isMiniLoader: true });

    const result = await createOrderSheet(
      { input: { order_item_list, selected_user_account_coupon_id_list } },
      {
        show_alert: false,
      },
    );
    if (result.errors) {
      if (result.errors![0].message !== ApiErrors.NOT_LOGGED_IN) {
        showZDSToastInOption(MESSAGES.UNKNOWN_ERROR);
      }
      return;
    }
    if (isBrowser()) {
      Router.push(
        { pathname: `/order-sheets/${result.data.createOrderSheet}`, query: { from } },
        from === 'product' ? { action: 'createOrderSheet' } : undefined,
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (
      error.extensions?.code === ApiErrorCodes.INSUFFICIENT_PRODUCT_QUANTITY ||
      error.extensions?.code === ApiErrorCodes.ORDER_ITEM_PRODUCT_ITEM_SOLDOUT
    ) {
      Dialog.alert(error?.description);
      if (from === 'cart') {
        window.location.reload();
      }
      return;
    }
    if (
      await handleNetworkError(error, async () =>
        createOrderSheetByOrderItemListAPI({ order_item_list, selected_user_account_coupon_id_list, from }),
      )
    ) {
      return;
    }
    Dialog.alert(error?.description || '알 수 없는 오류가 발생했습니다. 고객센터로 문의해주세요');
    showZDSToastInOption('주문 중에 에러가 발생했습니다');
  } finally {
    LoadingManager.close();
  }
};

// 장바구니 담기
const addCartAPI = async (props: AddCartOptions, callback?: <T>(result: T) => void) => {
  const { catalog_product_id, ...input } = props;
  try {
    LoadingManager.open({ isMiniLoader: true });
    const result = await addCartItemList({ input }, { show_alert: false });

    if (result.data && result.data.addCartItemList && callback) {
      callback(result);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    const is_network_error = await handleNetworkError(e, async () => await addCartAPI(props, callback));

    if (!is_network_error) {
      await Dialog.alert(e?.description || '장바구니 담기 실패하였습니다.');
    }
    if (e.extensions?.code === 'seller_blocked_user' && catalog_product_id) {
      redirectInquiries(catalog_product_id);
    }
  } finally {
    LoadingManager.close();
  }
};
